import { useState, useRef, useEffect } from "react";
import { C } from "../constants/colors";
import { LANG } from "../constants/lang";

export function StyledTutorText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return <span style={{whiteSpace:"pre-wrap"}}>{lines.map((line, li) => {
    const parts = [];
    const isConceptLine = /^📌\s*(Concept|सिद्धांत)\s*[:：]/i.test(line);
    const stepMatch = line.match(/\[STEP:\d+\/\d+\]/);
    // Remove step tag from display, show separately
    let cleanLine = line.replace(/\[STEP:\d+\/\d+\]/g, "").replace(/\[SOLVED\]/g, "").replace(/\[CONCEPT_CHECK\]/g, "").replace(/\[HESITATED\]/g, "").trimEnd();
    
    if (isConceptLine) {
      // Style entire concept line as a pill
      parts.push(
        <span key={`c${li}`} style={{display:"inline-block",background:C.primarySoft,borderRadius:8,padding:"4px 10px",fontSize:13,color:C.primaryDeep,lineHeight:1.5,marginBottom:2}}>
          {cleanLine}
        </span>
      );
    } else {
      // Highlight formulas inline: detect patterns like "F = ma", "v² = u² + 2as", etc.
      const formulaRegex = /([a-zA-Zα-ωΑ-Ω][a-zA-Z₀-₉²³⁰¹⁴⁵⁶⁷⁸⁹]*\s*[=]\s*[^\n,।?]*?)(?=[,।?\n]|$)/g;
      let lastIdx = 0;
      let match;
      let hasFormula = false;
      const tempParts = [];

      while ((match = formulaRegex.exec(cleanLine)) !== null) {
        const formula = match[0].trim();
        // Only style if it looks like a real formula (has = and some substance)
        if (formula.length > 3 && formula.includes("=")) {
          hasFormula = true;
          if (match.index > lastIdx) {
            tempParts.push(<span key={`t${li}-${lastIdx}`}>{cleanLine.slice(lastIdx, match.index)}</span>);
          }
          tempParts.push(
            <span key={`f${li}-${match.index}`} style={{fontFamily:"'Outfit', monospace",color:C.primaryMid,fontWeight:600,background:C.primarySoft,borderRadius:4,padding:"1px 5px",fontSize:14}}>
              {formula}
            </span>
          );
          lastIdx = match.index + match[0].length;
        }
      }
      if (hasFormula) {
        if (lastIdx < cleanLine.length) {
          tempParts.push(<span key={`e${li}`}>{cleanLine.slice(lastIdx)}</span>);
        }
        parts.push(...tempParts);
      } else {
        parts.push(<span key={`p${li}`}>{cleanLine}</span>);
      }
    }

    // Add step badge if present
    if (stepMatch) {
      const stepText = stepMatch[0].replace("[STEP:","").replace("]","");
      parts.push(
        <span key={`s${li}`} style={{display:"inline-block",marginLeft:6,fontSize:10,color:C.textMute,background:C.primarySoft,borderRadius:6,padding:"1px 6px",verticalAlign:"middle"}}>
          {stepText}
        </span>
      );
    }

    // Add newline between lines (except last)
    if (li < lines.length - 1) parts.push(<br key={`br${li}`}/>);
    return parts;
  })}</span>;
}

export function TypingBubble({ text, onDone }) {
  const [shown, setShown] = useState(""); const i = useRef(0);
  useEffect(() => { i.current=0; setShown(""); const iv=setInterval(()=>{ if(i.current<text.length){setShown(text.slice(0,i.current+1));i.current++;}else{clearInterval(iv);onDone?.();} },10); return ()=>clearInterval(iv); }, [text]);
  return <StyledTutorText text={shown}/>;
}
export function Dots({ label }) {
  return <div style={{display:"flex",alignItems:"center",gap:6}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.primary,animation:`td 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}{label&&<span style={{fontSize:13,color:C.textMute,marginLeft:4}}>{label}</span>}</div>;
}
export function RevisionText({ text, onDone }) {
  const [shown, setShown] = useState(""); const i = useRef(0);
  useEffect(() => { i.current=0; setShown(""); const iv=setInterval(()=>{ if(i.current<text.length){setShown(text.slice(0,i.current+1));i.current++;}else{clearInterval(iv);onDone?.();} },7); return ()=>clearInterval(iv); }, [text]);
  return <div>{shown.split("\n").map((line,idx)=>{
    const isF=/^[a-zA-Zα-ω]\s*=/.test(line.trim())&&!/^[•·]/.test(line.trim());
    const isH=/^[💡🧠📐🔢🔗]/.test(line); const isB=/^\s*[•·]/.test(line);
    if(isH) return <div key={idx} style={{fontWeight:700,fontSize:12,color:C.primary,marginTop:idx===0?0:18,marginBottom:6,letterSpacing:"0.08em",textTransform:"uppercase"}}>{line}</div>;
    if(isF) return <div key={idx} style={{background:C.primaryDark,color:"#fff",borderRadius:8,padding:"9px 15px",fontFamily:"monospace",fontSize:16,fontWeight:700,margin:"8px 0",display:"inline-block"}}>{line.trim()}</div>;
    if(isB) return <div key={idx} style={{paddingLeft:12,color:C.textMid,fontSize:14,lineHeight:1.8,fontFamily:"monospace",borderLeft:`2px solid ${C.border}`,margin:"2px 0 2px 4px"}}>{line}</div>;
    if(!line.trim()) return <div key={idx} style={{height:5}}/>;
    return <div key={idx} style={{color:C.text,fontSize:15,lineHeight:1.8}}>{line}</div>;
  })}</div>;
}
export function LangToggle({ lang, setLang }) {
  return <div style={{display:"flex",background:"#f1f5f9",borderRadius:24,padding:3}}>{["en","hi"].map(l=><button key={l} onClick={()=>setLang(l)} style={{padding:"5px 16px",borderRadius:20,border:"none",background:lang===l?C.primary:"transparent",color:lang===l?"#fff":C.textMute,fontSize:13,fontWeight:600,cursor:"pointer",transition:"all .2s",fontFamily:"inherit"}}>{l==="en"?"EN":"हि"}</button>)}</div>;
}

// ── Samajh Logo Icon ──────────────────────────────────────────────────────────
// ── Samajh Logo Icon ─────────────────────────────────────────────────────────
// Serif S with teal dot — scales via foreignObject trick using HTML+CSS
export function SamajhIcon({ size=40, dark=false }) {
  const bg = dark ? "#0f172a" : "#0d9488";
  const textColor = "#fff";
  const fontSize = Math.round(size * 0.58);
  const dotSize = Math.round(size * 0.18);
  const dotOffset = Math.round(size * 0.06);
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{display:"block",flexShrink:0}}>
      <rect width="40" height="40" rx="10" fill={bg}/>
      <text
        x="20" y="30"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="26"
        fontWeight="700"
        fill={textColor}
      >S</text>
      <circle cx="31" cy="28" r="4" fill="#5eead4"/>
    </svg>
  );
}

// ── Class Picker (dropdown) ───────────────────────────────────────────────────
export function ClassPicker({ value, onChange, lang }) {
  const t = LANG[lang];
  return (
    <div>
      <label style={{display:"block",fontSize:13,fontWeight:600,color:C.textMid,marginBottom:6}}>
        {t.classLabel}
      </label>
      <select
        value={value||""}
        onChange={(e)=>onChange(e.target.value?Number(e.target.value):null)}
        style={{
          width:"100%", padding:"11px 14px",
          background:"#f8fafc", border:`1.5px solid ${C.border}`,
          borderRadius:12, fontSize:15, color: value ? C.text : C.textMute,
          outline:"none", cursor:"pointer", fontFamily:"inherit",
          appearance:"none", WebkitAppearance:"none",
          backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center",
          paddingRight:40,
        }}
        onFocus={(e)=>e.target.style.borderColor=C.primary}
        onBlur={(e)=>e.target.style.borderColor=C.border}
      >
        <option value="">{lang==="hi"?"कक्षा चुनें":"Select your class"}</option>
        {[6,7,8,9,10].map(cls=>(
          <option key={cls} value={cls}>{lang==="hi"?`कक्षा ${cls}`:`Class ${cls}`}</option>
        ))}
      </select>
    </div>
  );
}
